import { FC, useState } from 'react';
import styled from 'styled-components';

import { TrackingStatusEnum } from 'order/wizard/orderTracking/enums/TrackingStatusEnum';
import {
  formatStyleDetails,
  formatTimeWindow,
  formatTrackingDate,
  getTrackingListStatus,
} from 'order/wizard/orderTracking/helpers/trackingHelpers';
import {
  StyleTracking,
  TrackingDetails,
} from 'order/wizard/orderTracking/interface/OrderTracking';
import { ButtonSecondary } from 'shared/components/Button';
import { PSmall, PXSmall } from 'shared/components/Typography';
import { Wrapper } from 'shared/components/Wrapper';
import { nevada } from 'shared/config/Colors';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrow-down.svg';
import { ReactComponent as TruckIcon } from 'assets/icons/truck.svg';

import { OrderTrackingTestEnum } from 'tests/enums/OrderTrackingTestEnums';
import { testId } from 'tests/utils';

import { TrackingDetailsPanel } from './TrackingDetailsPanel';
import { TrackingNumber } from './TrackingNumber';
import { TrackingStatusBadge } from './TrackingStatusBadge';

export const TrackingGrid = styled.div<{ withStyle: boolean }>`
  align-items: start;
  display: grid;
  gap: 16px;
  grid-template-columns: ${({ withStyle }) =>
    withStyle ? '1.6fr 2fr 1.5fr 1.3fr 90px' : '2.4fr 1.8fr 1.6fr 90px'};
  padding: 16px 24px;
`;

const RowContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.mischka};
`;

const Lines = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Line = styled(Wrapper)`
  align-items: center;
  display: flex;
  gap: 6px;
  min-height: 24px;
`;

const NotShippedLabel = styled(Line)`
  border: 1px dashed ${({ theme }) => theme.silverSand};
  border-radius: 6px;
  color: ${({ theme }) => theme.nevada};
  display: inline-flex;
  font-size: 12px;
  padding: 0 10px;

  svg {
    height: 14px;
    width: 14px;
  }
`;

const DetailsButton = styled(ButtonSecondary)`
  font-size: 12px;
  gap: 6px;
  height: 28px;
  padding: 0 12px;

  svg {
    transition: transform 300ms ease;
    width: 10px;
  }

  &[aria-expanded='true'] {
    background-color: ${({ theme }) => theme.kashmirBlue};
    color: ${({ theme }) => theme.white};

    svg {
      transform: rotate(180deg);
    }
  }
`;

const PanelWrapper = styled.div`
  padding: 0 24px 16px;
`;

const DeliveryLine: FC<{ details: TrackingDetails }> = ({ details }) => {
  if (details.isDelivered) {
    return (
      <Line>
        <PSmall>
          Delivered {formatTrackingDate(details.deliveredOnUtc, 'MMM d')}
        </PSmall>
        <PXSmall color={nevada}>
          {formatTrackingDate(details.deliveredOnUtc, 'h:mm a')}
        </PXSmall>
      </Line>
    );
  }

  if (details.estimatedDeliveryOnUtc) {
    return (
      <Line>
        <PSmall>
          Est.{' '}
          {formatTrackingDate(details.estimatedDeliveryOnUtc, 'EEE, MMM d')}
        </PSmall>
        <PXSmall color={nevada}>
          {formatTimeWindow(details.estimatedDeliveryWindow)}
        </PXSmall>
      </Line>
    );
  }

  return (
    <Line>
      <PSmall>—</PSmall>
    </Line>
  );
};

interface TrackingRowProps {
  styleTracking: StyleTracking;
  withStyle: boolean;
}

export const TrackingRow: FC<TrackingRowProps> = ({
  styleTracking: { style, trackingDetails },
  withStyle,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const boxCount = trackingDetails.length;

  return (
    <RowContainer {...testId(OrderTrackingTestEnum.ROW)}>
      <TrackingGrid withStyle={withStyle}>
        {withStyle && (
          <Wrapper>
            <PSmall bold>
              {style ? `#${style.number} ${style.name}` : 'No style'}
            </PSmall>
            {style && (
              <PXSmall color={nevada}>{formatStyleDetails(style)}</PXSmall>
            )}
          </Wrapper>
        )}

        {boxCount ? (
          <>
            <Lines>
              {trackingDetails.map((details, index) => (
                <Line key={details.trackingNumber}>
                  <TrackingNumber
                    trackingNumber={details.trackingNumber}
                    boxLabel={`Box ${index + 1} of ${boxCount}`}
                    highlighted={isExpanded && index === selectedIndex}
                  />
                </Line>
              ))}
            </Lines>

            <Lines>
              {trackingDetails.map((details) => (
                <DeliveryLine key={details.trackingNumber} details={details} />
              ))}
            </Lines>

            <Lines>
              {trackingDetails.map((details) => (
                <Line key={details.trackingNumber}>
                  <TrackingStatusBadge
                    status={getTrackingListStatus(details)}
                  />
                </Line>
              ))}
            </Lines>
          </>
        ) : (
          <>
            <Wrapper>
              <NotShippedLabel>
                <TruckIcon />
                Not shipped yet
              </NotShippedLabel>
            </Wrapper>

            <Line>
              <PSmall>—</PSmall>
            </Line>

            <Line>
              <TrackingStatusBadge status={TrackingStatusEnum.NOT_SHIPPED} />
            </Line>
          </>
        )}

        <Wrapper flex justifyEnd>
          {!!boxCount && (
            <DetailsButton
              type="button"
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded((prevState) => !prevState)}
            >
              Details
              <ArrowDownIcon />
            </DetailsButton>
          )}
        </Wrapper>
      </TrackingGrid>

      {isExpanded && (
        <PanelWrapper {...testId(OrderTrackingTestEnum.DETAILS_PANEL)}>
          <TrackingDetailsPanel
            trackingDetails={trackingDetails}
            selectedIndex={selectedIndex}
            onSelect={setSelectedIndex}
            totalWeightLabel={style ? 'Whole style' : 'Whole order'}
          />
        </PanelWrapper>
      )}
    </RowContainer>
  );
};
